import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useUser } from "@clerk/react";
import { formatDistanceToNow } from "date-fns";
import { toast } from "sonner";
import { Plus, Search, Wand2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { PageShell } from "@/components/layout/PageShell";
import { RunCounter } from "@/components/landing/RunCounter";
import { ProjectCard } from "@/components/dashboard/ProjectCard";
import { NameDialog } from "@/components/dashboard/NameDialog";
import { ConfirmDelete } from "@/components/dashboard/ConfirmDelete";
import { GuestImportBanner } from "@/components/dashboard/GuestImportBanner";
import { RcWizard } from "@/components/rc/RcWizard";
import { useApi, errorMessage } from "@/lib/api";
import { useMe } from "@/context/MeContext";

const EmptyState = ({ searching, onNew }) => (
  <div className="mt-4 rounded-2xl border border-dashed p-10 text-center" data-testid="projects-empty-state">
    <p className="font-display text-xl font-bold">{searching ? "No projects match that search" : "No projects yet"}</p>
    {!searching && (
      <>
        <p className="mt-1 text-sm text-muted-foreground">Set up your first robot: pick its drivetrain, ports and controls.</p>
        <Button onClick={onNew} className="mt-5 rounded-full" data-testid="empty-new-project-button"><Wand2 className="mr-1.5 h-4 w-4" />Set up a robot</Button>
      </>
    )}
  </div>
);

export default function Dashboard() {
  const api = useApi();
  const navigate = useNavigate();
  const { user } = useUser();
  const [projects, setProjects] = useState(null);
  const [query, setQuery] = useState("");
  const [renaming, setRenaming] = useState(null);
  const [toDelete, setToDelete] = useState(null);
  const [params, setParams] = useSearchParams();
  const [wizardOpen, setWizardOpen] = useState(params.has("setup"));
  const { me, refresh } = useMe();
  const limit = me?.project_limit;
  const atLimit = Boolean(projects && limit != null && projects.length >= limit);
  const limitText = `You've used all ${limit} project spaces. Delete a project to make room for a new one.`;

  useEffect(() => {
    api.get("/programs").then(setProjects).catch((e) => { toast.error(errorMessage(e)); setProjects([]); });
  }, [api]);

  useEffect(() => {
    if (params.has("setup")) setParams({}, { replace: true });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (atLimit) setWizardOpen(false);
  }, [atLimit]);

  const startNew = () => (atLimit ? toast.error(limitText) : setWizardOpen(true));

  const filtered = useMemo(
    () => (projects || []).filter((p) => p.name.toLowerCase().includes(query.toLowerCase())),
    [projects, query]
  );

  const create = async (config, name) => {
    try {
      const p = await api.post("/programs", { name: name.slice(0, 80), config });
      refresh();
      navigate(`/projects/${p.id}`);
    } catch (e) {
      toast.error(errorMessage(e));
    }
  };

  const importGuest = async (guest) => {
    const p = await api.post("/programs", { name: guest.name.slice(0, 80), config: guest.config });
    refresh();
    toast.success("Saved to your projects");
    navigate(`/projects/${p.id}`);
  };

  const rename = async (name) => {
    const p = await api.put(`/programs/${renaming.id}`, { name });
    setProjects((list) => list.map((x) => (x.id === p.id ? p : x)));
    toast.success("Renamed");
  };

  const duplicate = async (project) => {
    if (atLimit) return toast.error(limitText);
    try {
      const p = await api.post(`/programs/${project.id}/duplicate`);
      setProjects((list) => [p, ...list]);
      toast.success("Copy made");
    } catch (e) {
      toast.error(errorMessage(e));
    }
  };

  const remove = async () => {
    try {
      await api.del(`/programs/${toDelete.id}`);
      setProjects((list) => list.filter((x) => x.id !== toDelete.id));
      toast.success("Project deleted");
    } catch (e) {
      toast.error(errorMessage(e));
    }
    setToDelete(null);
  };

  return (
    <PageShell testId="dashboard-page">
      <RunCounter />
      <section className="mx-auto max-w-7xl px-4 py-10 sm:px-8 lg:py-14">
        <div className="fade-up flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <p className="eyebrow text-accent dark:text-primary">Welcome{user?.firstName ? `, ${user.firstName}` : ""}</p>
            <h1 className="mt-2 text-4xl font-extrabold tracking-tight sm:text-5xl">My projects</h1>
            <p className="mt-2 max-w-xl text-sm text-muted-foreground" data-testid="new-project-hint">Each project is one robot's driving setup: its drivetrain, motor ports, Xbox controls and attachments.</p>
          </div>
          <Button size="lg" className={`rounded-full ${atLimit ? "opacity-60" : ""}`} onClick={startNew} aria-disabled={atLimit} data-testid="new-project-button">
            <Plus className="mr-1.5 h-5 w-5" />New project
          </Button>
        </div>

        {atLimit && (
          <p className="fade-up mt-6 rounded-2xl border border-accent/40 bg-accent/10 px-4 py-3 text-sm" data-testid="limit-reached-notice">{limitText}</p>
        )}

        <GuestImportBanner onSave={importGuest} />

        <div className="fade-up delay-1 mt-10">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <h2 className="text-base font-semibold md:text-lg">Saved projects {projects && <span className="text-muted-foreground" data-testid="project-usage">({projects.length}{limit != null ? ` of ${limit}` : ""})</span>}</h2>
            <div className="relative sm:w-72">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search projects" className="rounded-full pl-9" data-testid="project-search-input" />
            </div>
          </div>

          {projects === null ? (
            <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
              {[0, 1, 2].map((i) => <Skeleton key={i} className="h-48 rounded-2xl" />)}
            </div>
          ) : filtered.length === 0 ? (
            <EmptyState searching={projects.length > 0} onNew={startNew} />
          ) : (
            <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4" data-testid="projects-grid">
              {filtered.map((p) => (
                <ProjectCard
                  key={p.id}
                  project={p}
                  edited={formatDistanceToNow(new Date(p.updated_at), { addSuffix: true })}
                  onOpen={() => navigate(`/projects/${p.id}`)}
                  onRename={() => setRenaming(p)}
                  onDuplicate={() => duplicate(p)}
                  onDelete={() => setToDelete(p)}
                />
              ))}
            </div>
          )}
        </div>
      </section>

      <NameDialog open={Boolean(renaming)} initialName={renaming?.name || ""} onClose={() => setRenaming(null)} onSubmit={rename} />
      <ConfirmDelete project={toDelete} onCancel={() => setToDelete(null)} onConfirm={remove} />
      <RcWizard open={wizardOpen} askName onClose={() => setWizardOpen(false)} onFinish={create} />
    </PageShell>
  );
}
